import { getDefaultFilamentIdForMaterialType } from "./filamentProfiles";
import { PushAllResponse } from "./printerResponse";
import { getColorDistance, hexToRgb } from "./colorUtils";

// Mirror BambuStudio virtual tray IDs
export const VIRTUAL_TRAY_MAIN_ID = 255;
export const VIRTUAL_TRAY_DEPUTY_ID = 254;
export const AMS_SLOTS_PER_UNIT = 4;
export const MAX_AMS_UNITS = 4;

/** Check if a slot index represents a virtual (external spool) tray */
export function isVirtualSlot(slot: number): boolean {
  return slot === VIRTUAL_TRAY_MAIN_ID || slot === VIRTUAL_TRAY_DEPUTY_ID;
}

/** Check if a slot index is valid (0-15 for AMS, or 254/255 for virtual) */
export function isValidAmsSlot(slot: number): boolean {
  return (slot >= 0 && slot <= MAX_AMS_UNITS * AMS_SLOTS_PER_UNIT - 1) || isVirtualSlot(slot);
}

/** Sanitize an AMS mapping array, replacing invalid values with -1 */
export function sanitizeAmsMapping(mapping: any[]): number[] {
  return Array.from({ length: mapping.length }, (_, i) => {
    const v = mapping[i];
    return (typeof v === 'number' && Number.isFinite(v)) ? v : -1;
  });
}

export enum AmsStatusMain {
  AMS_STATUS_MAIN_IDLE = 0x0,
  AMS_STATUS_MAIN_FILAMENT_CHANGE = 0x1,
  AMS_STATUS_MAIN_RFID_IDENTIFYING = 0x2,
  AMS_STATUS_MAIN_ASSIST = 0x3,
  AMS_STATUS_MAIN_CALIBRATION = 0x4,
  AMS_STATUS_MAIN_SELF_CHECK = 0x5,
  AMS_STATUS_MAIN_DEBUG = 0x6,
  AMS_STATUS_MAIN_UNKNOWN = 0x7,
}

/**
 * Filament change steps during AMS operation
 */
export enum FilamentStep {
  STEP_IDLE = 0,
  STEP_HEAT_NOZZLE = 0x02,
  STEP_CUT_FILAMENT = 0x03,
  STEP_PULL_CURR_FILAMENT = 0x04,
  STEP_PUSH_NEW_FILAMENT = 0x05,
  STEP_CONFIRM_EXTRUDED = 0x06,
  STEP_PURGE_OLD_FILAMENT = 0x07,
  STEP_CHECK_POSITION = 0x08
}

/**
 * Type of filament operation being performed
 */
export enum FilamentOperation {
  LOAD,
  UNLOAD
}

/**
 * Printer architecture types
 */
export enum PrinterArch {
  ARCH_CORE_XY = 0,
  ARCH_I3 = 1,
}

/**
 * Printer series types
 */
export enum PrinterSeries {
  SERIES_X1 = 0,
  SERIES_P1P = 1,
  SERIES_UNKNOWN = 2,
}

/**
 * Printing speed levels
 */
export enum PrintingSpeedLevel {
  SPEED_LEVEL_INVALID = 0,
  SPEED_LEVEL_SILENCE = 1,
  SPEED_LEVEL_NORMAL = 2,
  SPEED_LEVEL_RAPID = 3,
  SPEED_LEVEL_RAMPAGE = 4,
  SPEED_LEVEL_COUNT = 5
}

/**
 * Light effect types
 */
enum LightEffect {
  LIGHT_EFFECT_ON = 0,
  LIGHT_EFFECT_OFF = 1,
  LIGHT_EFFECT_FLASHING = 2,
  LIGHT_EFFECT_UNKNOWN = 3,
}

/**
 * Fan types
 */
enum FanType {
  COOLING_FAN = 1,
  BIG_COOLING_FAN = 2,
  CHAMBER_FAN = 3,
  EXHAUST_FAN = 4,
  FILTER_FAN = 5,
}

/**
 * SD card states
 */
export enum SdcardState {
  NO_SDCARD = 0,
  HAS_SDCARD_NORMAL = 1,
  HAS_SDCARD_ABNORMAL = 2,
  HAS_SDCARD_READONLY = 3,
  SDCARD_STATE_NUM = 4
}

/**
 * Active state types
 */
export enum ActiveState {
  NotActive = 0,
  Active = 1,
  UpdateToDate = 2
}

/**
 * Device modes
 */
export enum DeviceMode {
  DEVICE_MODE_FDM = 0x00000001,
  DEVICE_MODE_LASER = 0x00000010,
  DEVICE_MODE_CUT = 0x00000100,
}

/**
 * Door open check states
 */
export enum DoorOpenCheckState {
  DOOR_OPEN_CHECK_DISABLE = 0,
  DOOR_OPEN_CHECK_ENABLE_WARNING = 1,
  DOOR_OPEN_CHECK_ENABLE_PAUSE_PRINT = 2,
}

/**
 * Extruder switch states
 */
export enum ExtruderSwitchState {
  ES_IDLE = 0,
  ES_BUSY = 1,
  ES_SWITCHING = 2,
  ES_SWITCHING_FAILED = 3
}

/**
 * Nozzle flow types
 */
export enum NozzleFlowType {
  NONE_FLOWTYPE = 0,
  S_FLOW = 1,
  H_FLOW = 2
}

/**
 * Nozzle tool types
 */
export enum NozzleToolType {
  NONE_TOOLTYPE = 0,
  H_TOOL = 1,
  C_TOOL = 2
}

/**
 * Nozzle types
 */
export enum NozzleType {
  ntUndefine = 0,
  ntStainlessSteel = 1,
  ntHardenedSteel = 2,
  ntTungstenCarbide = 5
}

/**
 * Module version information
 */
export interface ModuleVersionInfo {
  name: string;
  product_name: string;
  sn: string;
  hw_ver: string;
  sw_ver: string;
  sw_new_ver: string;
  firmware_status: number;
}

/**
 * Extrusion ratio information
 */
export interface ExtrusionRatioInfo {
  name: string;
  setting_id: string;
  k: number;
  n: number;
}

/**
 * Firmware information
 */
export interface FirmwareInfo {
  module_type: string;
  version: string;
  url: string;
  name: string;
  description: string;
}

/**
 * Nozzle information
 */
export interface Nozzle {
  id: number;
  tool_type: NozzleToolType;
  nozzle_flow: NozzleFlowType;
  nozzle_type: NozzleType;
  diameter: number;
  max_temp: number;
  wear: number;
}

/**
 * Nozzle data
 */
export interface NozzleData {
  extder_exist: number;
  cut_exist: number;
  state: number;
  nozzles: Nozzle[];
}

/**
 * AMS slot information
 */
export interface AmsSlot {
  ams_id: string;
  slot_id: string;
}

/**
 * Extruder information
 */
export interface Extder {
  id: number;
  enable_change_nozzle: number;
  ext_has_filament: number;
  buffer_has_filament: number;
  nozzle_exist: number;
  filam_bak: number[];
  temp: number;
  target_temp: number;
  spre: AmsSlot;
  snow: AmsSlot;
  star: AmsSlot;
  ams_stat: number;
  rfid_stat: number;
  nozzle_id: number;
  target_nozzle_id: number;
  current_nozzle_type: NozzleType;
  current_nozzle_diameter: number;
  current_nozzle_flow_type: NozzleFlowType;
}

/**
 * Extruder data
 */
export interface ExtderData {
  current_extder_id: number;
  target_extder_id: number;
  total_extder_count: number;
  current_loading_extder_id: number;
  current_busy_for_loading: boolean;
  switch_extder_state: ExtruderSwitchState;
  extders: Extder[];
}

/**
 * Air mode information
 */
export interface AirMode {
  id: number;
  ctrl: number[];
  off: number[];
}

/**
 * Air parts information
 */
export interface AirParts {
  type: number;
  id: number;
  func: number;
  state: number;
  range_start: number;
  range_end: number;
}

/**
 * Air duct data
 */
export interface AirDuctData {
  curren_mode: number;
  modes: Map<number, AirMode>;
  parts: AirParts[];
}

/**
 * HMS item information
 */
interface HMSItem {
  module_id: number;
  module_num: number;
  part_id: number;
  reserved: number;
  msg_level: number;
  msg_code: number;
  already_read: boolean;
}

interface AMSTray {
  id: number;
  tray_color: string;
  tray_type: string;
  tray_sub_brands: string;
  is_bbl: boolean;
  read_done: boolean;
}

interface AMSUnit {
  id: number;
  tray: AMSTray[];
}

export class MachineObject {
  // Device Information
  dev_name: string = "";
  dev_ip: string = "";
  dev_id: string = "";
  dev_connection_type: string = "";
  dev_connection_name: string = "";
  local_use_ssl_for_mqtt: boolean = true;
  local_use_ssl_for_ftp: boolean = true;
  m_busy_for_select_extruder: boolean = false;
  subscribe_counter: number = 3;

  // Printer Information
  printer_thumbnail_img: string = "";
  monitor_upgrade_printer_img: string = "";
  product_name: string = "";
  bind_user_name: string = "";
  bind_user_id: string = "";
  bind_state: string = "";
  bind_sec_link: string = "";
  bind_ssdp_version: string = "";
  last_alive: number = 0;
  m_is_online: boolean = false;
  m_lan_mode_connection_state: boolean = false;
  m_set_ctt_dlg: boolean = false;
  parse_msg_count: number = 0;
  keep_alive_count: number = 0;
  last_update_time: number = 0;
  last_utc_time: number = 0;
  last_keep_alive: number = 0;
  last_push_time: number = 0;
  last_request_push: number = 0;
  last_request_start: number = 0;
  m_active_state: number = 0;
  is_tunnel_mqtt: boolean = false;

  // AMS Status
  ams_status_main: AmsStatusMain = AmsStatusMain.AMS_STATUS_MAIN_UNKNOWN;
  ams_status_sub: number = 0;
  hw_switch_state: number = 0;
  m_tray_now: string = "";
  m_tray_tar: string = "";
  m_ams_id: string = "";
  m_tray_id: string = "";

  // AMS Physical State
  ams_exist_bits: number = 0;
  tray_exist_bits: number = 0;
  tray_is_bbl_bits: number = 0;
  tray_read_done_bits: number = 0;
  tray_reading_bits: number = 0;
  ams_rfid_status: number = 0;
  ams_insert_flag: boolean = false;
  ams_power_on_flag: boolean = false;
  ams_calibrate_remain_flag: boolean = false;
  ams_auto_switch_filament_flag: boolean = false;
  ams_air_print_status: boolean = false;
  ams_support_virtual_tray: boolean = true;
  ams_user_setting_start: number = 0;
  ams_switch_filament_start: number = 0;
  ams_version: number = 0;
  ams_cali_id: number = 0;
  ams_cali_stat: number = 0;
  is_ams_need_update: boolean = false;

  // Temperature & Environment
  bed_temp: number = 0;
  bed_temp_target: number = 0;
  chamber_temp: number = 0;
  chamber_temp_target: number = 0;
  chamber_temp_edit_min: number = 0;
  chamber_temp_edit_max: number = 60;
  chamber_temp_switch_heat: number = Number.MAX_SAFE_INTEGER;
  frame_temp: number = 0;
  heatbreak_fan_speed: number = 0;
  cooling_fan_speed: number = 0;
  big_fan1_speed: number = 0;
  big_fan2_speed: number = 0;
  fan_gear: number = 0;

  // Print Status
  print_status: string = "";
  print_type: string = "";
  nozzle: number = 0;
  is_220V_voltage: boolean = false;
  mc_print_stage: number = 0;
  mc_print_sub_stage: number = 0;
  mc_print_error_code: number = 0;
  mc_print_line_number: number = 0;
  mc_print_percent: number = 0;
  mc_left_time: number = 0;
  last_mc_print_stage: number = 0;
  home_flag: number = 0;
  print_error: number = 0;
  curr_layer: number = 0;
  total_layers: number = 0;
  is_support_layer_num: boolean = false;
  nozzle_blob_detection_enabled: boolean = false;
  nozzle_blob_detection_hold_start: number = 0;

  // Lights
  chamber_light: LightEffect = LightEffect.LIGHT_EFFECT_OFF;
  work_light: LightEffect = LightEffect.LIGHT_EFFECT_OFF;

  // Upgrade & Firmware
  upgrade_force_upgrade: boolean = false;
  upgrade_new_version: boolean = false;
  upgrade_consistency_request: boolean = false;
  upgrade_display_state: number = 0;
  upgrade_display_hold_count: number = 0;
  firmware_type: number = 0;
  lifecycle: number = 0;
  upgrade_progress: string = "";
  upgrade_message: string = "";
  upgrade_status: string = "";
  upgrade_module: string = "";
  ams_new_version_number: string = "";
  ota_new_version_number: string = "";
  ahb_new_version_number: string = "";
  get_version_retry: number = 0;
  upgrade_err_code: number = 0;

  // Module Versions
  air_pump_version_info: ModuleVersionInfo = {} as ModuleVersionInfo;
  laser_version_info: ModuleVersionInfo = {} as ModuleVersionInfo;
  cutting_module_version_info: ModuleVersionInfo = {} as ModuleVersionInfo;
  module_vers: Map<string, ModuleVersionInfo> = new Map();
  new_ver_list: Map<string, ModuleVersionInfo> = new Map();
  extrusion_ratio_map: Map<string, ExtrusionRatioInfo> = new Map();
  m_new_ver_list_exist: boolean = false;
  firmware_list: FirmwareInfo[] = [];

  // Extruder & Nozzle Data
  is_enable_np: boolean = false;
  is_enable_ams_np: boolean = false;
  m_extder_data: ExtderData = {} as ExtderData;
  m_nozzle_data: NozzleData = {} as NozzleData;

  // Air Duct Data
  m_air_duct_data: AirDuctData = {} as AirDuctData;

  // Network
  wifi_signal: string = "";
  link_th: string = "";
  link_ams: string = "";
  network_wired: boolean = false;

  // Camera & AI
  has_ipcam: boolean = false;
  camera_recording: boolean = false;
  camera_recording_when_printing: boolean = false;
  camera_timelapse: boolean = false;
  camera_recording_ctl_start: number = 0;
  camera_timelapse_hold_count: number = 0;
  camera_resolution_hold_count: number = 0;
  camera_resolution: string = "";
  camera_resolution_supported: string[] = [];
  xcam_first_layer_inspector: boolean = false;
  xcam_first_layer_hold_start: number = 0;
  local_rtsp_url: string = "";
  tutk_state: string = "";
  liveview_local: number = 0;
  liveview_remote: number = 0;
  file_local: number = 0;
  file_remote: number = 0;
  virtual_camera: boolean = false;
  xcam_ai_monitoring: boolean = false;
  xcam_ai_monitoring_hold_start: number = 0;
  xcam_ai_monitoring_sensitivity: string = "";
  xcam_buildplate_marker_detector: boolean = false;
  xcam_buildplate_marker_hold_start: number = 0;
  xcam_auto_recovery_step_loss: boolean = false;
  xcam_allow_prompt_sound: boolean = false;
  xcam_filament_tangle_detect: boolean = false;
  xcam_auto_recovery_hold_start: number = 0;
  xcam_prompt_sound_hold_start: number = 0;
  xcam_filament_tangle_detect_hold_start: number = 0;

  // Support Flags
  is_support_chamber_edit: boolean = false;
  is_support_extrusion_cali: boolean = false;
  is_support_first_layer_inspect: boolean = false;
  is_support_ai_monitoring: boolean = false;
  is_support_lidar_calibration: boolean = false;
  is_support_build_plate_marker_detect: boolean = false;
  is_support_pa_calibration: boolean = false;
  is_support_flow_calibration: boolean = false;
  is_support_auto_flow_calibration: boolean = false;
  is_support_print_without_sd: boolean = false;
  is_support_print_all: boolean = false;
  is_support_send_to_sdcard: boolean = false;
  is_support_aux_fan: boolean = false;
  is_support_chamber_fan: boolean = false;
  is_support_filament_backup: boolean = false;
  is_support_show_filament_backup: boolean = false;
  is_support_timelapse: boolean = false;
  is_support_update_remain: boolean = false;
  is_support_bed_leveling: number = 0;
  is_support_auto_recovery_step_loss: boolean = false;
  is_support_ams_humidity: boolean = false;
  is_support_prompt_sound: boolean = false;
  is_support_filament_tangle_detect: boolean = false;
  is_support_1080dpi: boolean = false;
  is_support_cloud_print_only: boolean = false;
  is_support_command_ams_switch: boolean = false;
  is_support_mqtt_alive: boolean = false;
  is_support_tunnel_mqtt: boolean = false;
  is_support_motor_noise_cali: boolean = false;
  is_support_nozzle_offset_cali: boolean = false;
  is_support_high_tempbed_cali: boolean = false;
  is_support_wait_sending_finish: boolean = false;
  is_support_user_preset: boolean = false;
  is_support_nozzle_blob_detection: boolean = false;
  is_support_air_print_detection: boolean = false;
  is_support_agora: boolean = false;
  is_support_upgrade_kit: boolean = false;
  is_support_filament_setting_inprinting: boolean = false;
  is_support_internal_timelapse: boolean = false;
  is_support_command_homing: boolean = false;
  is_support_brtc: boolean = false;
  is_support_door_open_check: boolean = false;
  is_support_save_remote_print_file_to_storage: boolean = false;

  // Door Open Check
  xcam_door_open_check: DoorOpenCheckState = DoorOpenCheckState.DOOR_OPEN_CHECK_DISABLE;
  xcam_door_open_check_start_time: number = 0;
  xcam__save_remote_print_file_to_storage: boolean = false;
  xcam__save_remote_print_file_to_storage_start_time: number = 0;

  // Hardware Limits
  bed_temperature_limit: number = -1;
  nozzle_temp_range: number[] = [];
  bed_temp_range: number[] = [];
  sdcard_state: SdcardState = SdcardState.NO_SDCARD;

  // HMS
  hms_list: HMSItem[] = [];

  // Device Mode
  m_device_mode: DeviceMode = DeviceMode.DEVICE_MODE_FDM;

  // Print Job
  m_plate_index: number = -1;
  m_gcode_file: string = "";
  gcode_file_prepare_percent: number = 0;
  request_model_result: number = 0;
  get_model_mall_result_need_retry: boolean = false;
  obj_subtask_id: string = "";
  subtask_name: string = "";
  file_model_download: boolean = false;

  // Nozzle Settings
  nozzle_setting_hold_count: number = 0;
  nozzle_selected_count: number = 0;
  flag_update_nozzle: boolean = true;
  targ_nozzle_id_from_pc: number = -1;
  installed_upgrade_kit: boolean = false;

  // Calibration
  last_cali_version: number = -1;
  cali_version: number = -1;
  cali_selected_nozzle_dia: number = 0;
  cache_flow_ratio: number = 0;
  cali_finished: boolean = true;
  flow_ratio_calibration_type: number = 0;
  manual_pa_cali_method: number = 0;
  has_get_pa_calib_tab: boolean = false;
  request_tab_from_bbs: boolean = false;
  get_pa_calib_result: boolean = false;
  get_flow_calib_result: boolean = false;
  stage_list_info: number[] = [];
  stage_curr: number = 0;
  m_push_count: number = 0;
  m_full_msg_count: number = 0;
  calibration_done: boolean = false;

  // Online Status
  online_rfid: boolean = false;
  online_ahb: boolean = false;
  online_version: number = -1;
  last_online_version: number = -1;

  // Firmware & Version
  m_firmware_valid: boolean = false;
  nozzle_target_temper: number = 0;

  // AMS Units
  amsList: AMSUnit[] = [];

  constructor(state?: PushAllResponse) {
    if (state) {
      this.parseState(state);
    }
  }

  /**
   * Check if printer has any AMS units
   */
  hasAms(): boolean {
    return this.ams_exist_bits !== 0;
  }

  /**
   * Get number of AMS units
   */
  getAmsCount(): number {
    let count = 0;
    for (let i = 0; i < 32; i++) {
      if (this.ams_exist_bits & (1 << i)) count++;
    }
    return count;
  }

  /**
   * Check if specific AMS exists
   */
  isAmsExist(ams_id: number): boolean {
    return (this.ams_exist_bits & (1 << ams_id)) !== 0;
  }

  /**
   * Check if tray exists in specific AMS
   */
  isTrayExist(ams_id: number, tray_id: number): boolean {
    const globalIndex = (ams_id * 4) + tray_id;
    return (this.tray_exist_bits & (1 << globalIndex)) !== 0;
  }

  /**
   * Check if tray contains Bambu Lab filament
   */
  isBambuFilament(ams_id: number, tray_id: number): boolean {
    const globalIndex = (ams_id * 4) + tray_id;
    return (this.tray_is_bbl_bits & (1 << globalIndex)) !== 0;
  }

  /**
   * Check if tray RFID has been read
   */
  isRfidReadDone(ams_id: number, tray_id: number): boolean {
    const globalIndex = (ams_id * 4) + tray_id;
    return (this.tray_read_done_bits & (1 << globalIndex)) !== 0;
  }

  /**
   * Parse AMS status from raw status value
   * @param ams_status Raw AMS status value from printer
   */
  private _parse_ams_status(ams_status: number) {
    this.ams_status_sub = ams_status & 0xFF;
    const ams_status_main_int = (ams_status & 0xFF00) >> 8;

    switch(ams_status_main_int) {
      case AmsStatusMain.AMS_STATUS_MAIN_IDLE:
        this.ams_status_main = AmsStatusMain.AMS_STATUS_MAIN_IDLE;
        break;
      case AmsStatusMain.AMS_STATUS_MAIN_FILAMENT_CHANGE:
        this.ams_status_main = AmsStatusMain.AMS_STATUS_MAIN_FILAMENT_CHANGE;
        break;
      case AmsStatusMain.AMS_STATUS_MAIN_RFID_IDENTIFYING:
        this.ams_status_main = AmsStatusMain.AMS_STATUS_MAIN_RFID_IDENTIFYING;
        break;
      case AmsStatusMain.AMS_STATUS_MAIN_ASSIST:
        this.ams_status_main = AmsStatusMain.AMS_STATUS_MAIN_ASSIST;
        break;
      case AmsStatusMain.AMS_STATUS_MAIN_CALIBRATION:
        this.ams_status_main = AmsStatusMain.AMS_STATUS_MAIN_CALIBRATION;
        break;
      case AmsStatusMain.AMS_STATUS_MAIN_SELF_CHECK:
        this.ams_status_main = AmsStatusMain.AMS_STATUS_MAIN_SELF_CHECK;
        break;
      case AmsStatusMain.AMS_STATUS_MAIN_DEBUG:
        this.ams_status_main = AmsStatusMain.AMS_STATUS_MAIN_DEBUG;
        break;
      default:
        this.ams_status_main = AmsStatusMain.AMS_STATUS_MAIN_UNKNOWN;
    }
  }

  /**
   * Decode and apply `tray_now` semantics to mirror BambuStudio.
   * - 255 (VIRTUAL_TRAY_MAIN_ID): external spool main
   * - 254 (VIRTUAL_TRAY_DEPUTY_ID): external spool deputy
   * - Otherwise: encode AMS/tray in bits: ams_id = val >> 2, tray_id = val & 0x3
   */
  private _parse_tray_now(tray_now: string | undefined): void {
    if (!tray_now || tray_now.length === 0) return;

    try {
      const trayNowInt = parseInt(tray_now, 10);

      if (trayNowInt === VIRTUAL_TRAY_MAIN_ID) {
        // External main: clear snow mapping unless transitioning from deputy
        this.m_ams_id = "0";
        this.m_tray_id = "0";

        const mainExtruder = this.m_extder_data?.extders?.[0];
        if (mainExtruder) {
          if (this.m_tray_now === String(VIRTUAL_TRAY_DEPUTY_ID)) {
            mainExtruder.snow.ams_id = String(VIRTUAL_TRAY_MAIN_ID);
            mainExtruder.snow.slot_id = "0";
          } else {
            mainExtruder.snow.ams_id = "";
            mainExtruder.snow.slot_id = "";
          }
        }
      } else if (trayNowInt === VIRTUAL_TRAY_DEPUTY_ID) {
        // External deputy: point snow to virtual main slot
        const mainExtruder = this.m_extder_data?.extders?.[0];
        if (mainExtruder) {
          mainExtruder.snow.ams_id = String(VIRTUAL_TRAY_MAIN_ID);
          mainExtruder.snow.slot_id = "0";
        }
      } else {
        // Normal AMS slot: derive AMS and tray IDs
        const amsId = (trayNowInt >= 0x80 && trayNowInt <= 0x87)
          ? trayNowInt
          : (trayNowInt >> 2);
        const trayId = trayNowInt & 0x3;

        this.m_ams_id = String(amsId);
        this.m_tray_id = String(trayId);

        const mainExtruder = this.m_extder_data?.extders?.[0];
        if (mainExtruder) {
          mainExtruder.snow.ams_id = this.m_ams_id;
          mainExtruder.snow.slot_id = this.m_tray_id;
        }
      }
    } catch {
      // Ignore parse errors; leave existing ids untouched
    }

    this.m_tray_now = tray_now;
  }

  /**
   * Parse full printer state from PushAllResponse
   * @param state Printer state response
   */
  parseState(state: PushAllResponse) {
    // Parse AMS status if available (must handle 0 as valid)
    if (state.ams_status !== undefined) {
      this._parse_ams_status(state.ams_status);
    }
    
    // Parse hardware switch state and current tray
    const printerState = state;

    if (printerState.hw_switch_state !== undefined) {
      this.hw_switch_state = printerState.hw_switch_state;
    }
    
    if (printerState.ams?.tray_now !== undefined) {
      this._parse_tray_now(printerState.ams.tray_now);
    }

    if (printerState.ams?.tray_tar !== undefined) {
      this.m_tray_tar = printerState.ams.tray_tar;
    }

    // Parse AMS physical state
    if (state.ams?.ams_exist_bits) {
      this.ams_exist_bits = parseInt(state.ams.ams_exist_bits, 16);
    }

    if (state.ams?.tray_exist_bits) {
      this.tray_exist_bits = parseInt(state.ams.tray_exist_bits, 16);
    }

    if (state.ams?.tray_is_bbl_bits) {
      this.tray_is_bbl_bits = parseInt(state.ams.tray_is_bbl_bits, 16);
    }

    if (state.ams?.tray_read_done_bits) {
      this.tray_read_done_bits = parseInt(state.ams.tray_read_done_bits, 16);
    }

    if (state.ams?.version !== undefined) {
      this.ams_version = state.ams.version;
    }

    // Parse Print Status
    if (state.gcode_state !== undefined) {
      this.print_status = state.gcode_state;
    }

    // Parse AMS units and trays
    this.amsList = [];
    if (state.ams?.ams) {
      state.ams.ams.forEach((ams, amsIndex) => {
        if (!ams?.tray) return;

        const amsUnit: AMSUnit = {
          id: amsIndex,
          tray: []
        };

        ams.tray.forEach((tray, trayIndex) => {
          if (!tray) return;

          const globalIndex = (amsIndex * 4) + trayIndex;
          amsUnit.tray.push({
            id: trayIndex,
            tray_color: tray.tray_color || '',
            tray_type: tray.tray_type || '',
            tray_sub_brands: tray.tray_sub_brands || '',
            is_bbl: this.isBambuFilament(amsIndex, trayIndex),
            read_done: this.isRfidReadDone(amsIndex, trayIndex)
          });
        });

        this.amsList.push(amsUnit);
      });
    }
  }

  /**
   * Check if AMS is waiting for filament load confirmation
   * @returns true if waiting for confirmation, false otherwise
   */
  isWaitingFilamentConfirmation(): boolean {
    return this.ams_status_main === AmsStatusMain.AMS_STATUS_MAIN_FILAMENT_CHANGE && 
           this.ams_status_sub === 0x06;
  }

  /**
   * Check if filament is at extruder
   * @returns true if filament is detected at extruder
   */
  isFilamentAtExtruder(): boolean {
    return this.hw_switch_state === 1;
  }

  /**
   * Static method to check if printer state indicates waiting for filament confirmation
   * @param state Full printer state response
   * @returns true if waiting for confirmation, false otherwise
   */
  static isWaitingFilamentConfirmation(state: PushAllResponse): boolean {
    const machine = new MachineObject(state);
    return machine.isWaitingFilamentConfirmation();
  }

  /**
   * Get the current filament step
   * @returns Current FilamentStep or STEP_IDLE if not in filament change
   */
  getCurrentFilamentStep(): FilamentStep {
    if (this.ams_status_main !== AmsStatusMain.AMS_STATUS_MAIN_FILAMENT_CHANGE) {
      return FilamentStep.STEP_IDLE;
    }
    return this.ams_status_sub as FilamentStep;
  }

  /**
   * Get human-readable status text for the current filament operation
   * @returns Status text describing current operation
   */
  getFilamentStatusText(): string {
    if (this.ams_status_main !== AmsStatusMain.AMS_STATUS_MAIN_FILAMENT_CHANGE) {
      return "Idle";
    }

    switch (this.ams_status_sub) {
      case FilamentStep.STEP_HEAT_NOZZLE:
        return "Heating nozzle...";
      case FilamentStep.STEP_CUT_FILAMENT:
        return "Cutting filament...";
      case FilamentStep.STEP_PULL_CURR_FILAMENT:
        return "Pulling current filament...";
      case FilamentStep.STEP_PUSH_NEW_FILAMENT:
        return "Pushing new filament...";
      case FilamentStep.STEP_CONFIRM_EXTRUDED:
        return "Please confirm filament is loaded correctly";
      case FilamentStep.STEP_PURGE_OLD_FILAMENT:
        return "Purging old filament...";
      case FilamentStep.STEP_CHECK_POSITION:
        return "Checking position...";
      default:
        return "Unknown state";
    }
  }

  /**
   * Check if currently performing a filament change operation
   * @returns true if in filament change mode
   */
  isInFilamentChange(): boolean {
    return this.ams_status_main === AmsStatusMain.AMS_STATUS_MAIN_FILAMENT_CHANGE;
  }

  /**
   * Get the current filament operation type
   * @returns FilamentOperation.LOAD or FilamentOperation.UNLOAD
   */
  getFilamentOperation(): FilamentOperation {
    // Mirror BambuStudio: unload when target is 255
    return this.m_tray_tar === String(VIRTUAL_TRAY_MAIN_ID)
      ? FilamentOperation.UNLOAD
      : FilamentOperation.LOAD;
  }

  /**
   * Get AMS unit by ID
   * @param ams_id AMS unit ID
   * @returns AMSUnit if found, undefined otherwise
   */
  getAmsUnit(ams_id: number): AMSUnit | undefined {
    return this.amsList.find(ams => ams.id === ams_id);
  }

  /**
   * Get tray by AMS ID and tray ID
   * @param ams_id AMS unit ID
   * @param tray_id Tray ID within the AMS unit
   * @returns AMSTray if found, undefined otherwise
   */
  getTray(ams_id: number, tray_id: number): AMSTray | undefined {
    const ams = this.getAmsUnit(ams_id);
    return ams?.tray.find(t => t.id === tray_id);
  }

  /**
   * Check if two trays are compatible (same type and color)
   * @param ams_id1 First AMS unit ID
   * @param tray_id1 First tray ID
   * @param ams_id2 Second AMS unit ID
   * @param tray_id2 Second tray ID
   * @returns true if trays are compatible
   */
  areTraysCompatible(ams_id1: number, tray_id1: number, ams_id2: number, tray_id2: number): boolean {
    const tray1 = this.getTray(ams_id1, tray_id1);
    const tray2 = this.getTray(ams_id2, tray_id2);

    if (!tray1 || !tray2) return false;

    return tray1.tray_type === tray2.tray_type && 
           tray1.tray_color === tray2.tray_color;
  }

  /**
   * Find all compatible trays for a given tray
   * @param ams_id AMS unit ID
   * @param tray_id Tray ID
   * @returns Array of compatible tray locations
   */
  findCompatibleTrays(ams_id: number, tray_id: number): Array<{ams_id: number, tray_id: number}> {
    const sourceTray = this.getTray(ams_id, tray_id);
    if (!sourceTray) return [];

    const compatible: Array<{ams_id: number, tray_id: number}> = [];

    this.amsList.forEach(ams => {
      ams.tray.forEach(tray => {
        if (ams.id === ams_id && tray.id === tray_id) return; // Skip self

        if (tray.tray_type === sourceTray.tray_type && 
            tray.tray_color === sourceTray.tray_color) {
          compatible.push({ams_id: ams.id, tray_id: tray.id});
        }
      });
    });

    return compatible;
  }

  /**
   * Get the state of a specific tray
   * @param ams_id AMS unit ID
   * @param tray_id Tray ID
   * @returns Object containing tray state information
   */
  getTrayState(ams_id: number, tray_id: number): {
    exists: boolean;
    isBambuLab: boolean;
    rfidRead: boolean;
    tray?: AMSTray
  } {
    return {
      exists: this.isTrayExist(ams_id, tray_id),
      isBambuLab: this.isBambuFilament(ams_id, tray_id),
      rfidRead: this.isRfidReadDone(ams_id, tray_id),
      tray: this.getTray(ams_id, tray_id)
    };
  }

  /**
   * Parses the home_flag bitfield from BambuStudio and returns an object with all extracted flags.
   * @param home_flag The integer bitfield from the printer status (e.g., from pushall response)
   * @returns An object with all extracted flags as booleans or numbers
   */
  static parseHomeFlag(home_flag: number) {
    return {
      is220V:               ((home_flag >> 3) & 1) !== 0,
      xcamAutoRecoveryStepLoss: ((home_flag >> 4) & 1) !== 0,
      cameraRecording:      ((home_flag >> 5) & 1) !== 0,
      amsCalibrateRemain:   ((home_flag >> 7) & 1) !== 0,
      sdcardState:          (home_flag >> 8) & 0x11, // bits 8 and 4
      amsAutoSwitchFilament:((home_flag >> 10) & 1) !== 0,
      xcamAllowPromptSound: ((home_flag >> 17) & 1) !== 0,
      isSupportPromptSound: ((home_flag >> 18) & 1) !== 0,
      isSupportFilamentTangleDetect: ((home_flag >> 19) & 1) !== 0,
      xcamFilamentTangleDetect: ((home_flag >> 20) & 1) !== 0,
      isSupportMotorNoiseCali: ((home_flag >> 21) & 1) !== 0,
      isSupportUserPreset:  ((home_flag >> 22) & 1) !== 0,
      nozzleBlobDetectionEnabled: ((home_flag >> 24) & 1) !== 0,
      isSupportNozzleBlobDetection: ((home_flag >> 25) & 1) !== 0,
      installedPlus:        ((home_flag >> 26) & 1) !== 0,
      supportedPlus:        ((home_flag >> 27) & 1) !== 0,
      amsAirPrintStatus:    ((home_flag >> 28) & 1) !== 0,
      isSupportAirPrintDetection: ((home_flag >> 29) & 1) !== 0,
      isSupportAgora:       ((home_flag >> 30) & 1) !== 0,
    };
  }



  // ===== DEVICE INFORMATION METHODS =====

  /**
   * Get the device IP address
   */
  getDevIp(): string {
    return this.dev_ip;
  }

  /**
   * Set the device IP address
   */
  setDevIp(ip: string): void {
    this.dev_ip = ip;
  }

  /**
   * Get the connection type (lan | cloud)
   */
  getConnectionType(): string {
    return this.dev_connection_type;
  }

  /**
   * Check if device has access rights
   */
  hasAccessRight(): boolean {
    return this.getAccessCode().length > 0;
  }

  /**
   * Get access code (placeholder - would need implementation)
   */
  getAccessCode(): string {
    return ""; // TODO: Implement access code storage
  }

  /**
   * Set access code (placeholder - would need implementation)
   */
  setAccessCode(code: string, onlyRefresh: boolean = true): void {
    // TODO: Implement access code storage
  }

  /**
   * Check if printer is in LAN mode
   */
  isLanModePrinter(): boolean {
    return this.dev_connection_type === "lan";
  }

  /**
   * Check if device is local (has IP address)
   */
  isLocal(): boolean {
    return this.dev_ip.length > 0;
  }

  /**
   * Check if device is available (not occupied)
   */
  isAvailable(): boolean {
    return this.bind_state === "free";
  }

  /**
   * Set LAN mode connection state
   */
  setLanModeConnectionState(state: boolean): void {
    this.m_lan_mode_connection_state = state;
  }

  /**
   * Get LAN mode connection state
   */
  getLanModeConnectionState(): boolean {
    return this.m_lan_mode_connection_state;
  }

  /**
   * Set online state
   */
  setOnlineState(onOff: boolean): void {
    this.m_is_online = onOff;
  }

  /**
   * Check if device is online
   */
  isOnline(): boolean {
    return this.m_is_online;
  }

  /**
   * Check if device is connected
   */
  isConnected(): boolean {
    return this.m_is_online && this.dev_ip.length > 0;
  }

  /**
   * Check if device is connecting
   */
  isConnecting(): boolean {
    return this.m_is_online && this.dev_ip.length === 0;
  }

  // ===== PRINTER INFORMATION METHODS =====

  /**
   * Get printer series
   */
  getPrinterSeries(): PrinterSeries {
    if (this.product_name.includes("X1")) {
      return PrinterSeries.SERIES_X1;
    } else if (this.product_name.includes("P1")) {
      return PrinterSeries.SERIES_P1P;
    }
    return PrinterSeries.SERIES_UNKNOWN;
  }

  /**
   * Get printer architecture
   */
  getPrinterArch(): PrinterArch {
    if (this.product_name.includes("X1") || this.product_name.includes("P1")) {
      return PrinterArch.ARCH_CORE_XY;
    }
    return PrinterArch.ARCH_I3;
  }

  /**
   * Check if printer is CoreXY architecture
   */
  isCoreXY(): boolean {
    return this.getPrinterArch() === PrinterArch.ARCH_CORE_XY;
  }

  /**
   * Check if printer is series X1
   */
  isSeriesX1(): boolean {
    return this.getPrinterSeries() === PrinterSeries.SERIES_X1;
  }

  /**
   * Check if printer is series P1
   */
  isSeriesP1(): boolean {
    return this.getPrinterSeries() === PrinterSeries.SERIES_P1P;
  }

  /**
   * Get printer series as string
   */
  getPrinterSeriesStr(): string {
    switch (this.getPrinterSeries()) {
      case PrinterSeries.SERIES_X1:
        return "X1";
      case PrinterSeries.SERIES_P1P:
        return "P1";
      default:
        return "Unknown";
    }
  }

  /**
   * Check if printer is FDM type
   */
  isFdmType(): boolean {
    return this.m_device_mode === DeviceMode.DEVICE_MODE_FDM;
  }

  // ===== PRINT CONTROL METHODS =====

  /**
   * Check if printer can print
   */
  canPrint(): boolean {
    return this.print_status === "IDLE" || this.print_status === "FINISH";
  }

  /**
   * Check if printer can resume printing
   */
  canResume(): boolean {
    return this.print_status === "PAUSE";
  }

  /**
   * Check if printer can pause printing
   */
  canPause(): boolean {
    return this.print_status === "RUNNING";
  }

  /**
   * Check if printer can abort printing
   */
  canAbort(): boolean {
    return MachineObject.isInPrintingStatus(this.print_status);
  }

  /**
   * Check if printer is in printing state
   */
  isInPrinting(): boolean {
    return this.print_status === "RUNNING" || this.print_status === "PAUSE";
  }

  /**
   * Check if printer is in printing pause state
   */
  isInPrintingPause(): boolean {
    return this.print_status === "PAUSE";
  }

  /**
   * Check if printer is in prepare state
   */
  isInPrepare(): boolean {
    return this.print_status === "PREPARE";
  }

  /**
   * Check if printing is finished
   */
  isPrintingFinished(): boolean {
    return this.print_status === "FINISH";
  }

  /**
   * Check if printer is in system printing state
   */
  isSystemPrinting(): boolean {
    return this.print_status === "RUNNING" || this.print_status === "PAUSE";
  }

  /**
   * Set print state
   */
  setPrintState(status: string): void {
    this.print_status = status;
  }

  /**
   * Static method to check if status indicates printing
   */
  static isInPrintingStatus(status: string): boolean {
    return status === "PAUSE" ||
           status === "RUNNING" ||
           status === "SLICING" ||
           status === "PREPARE";
  }

  // ===== TEMPERATURE METHODS =====

  /**
   * Get bed temperature
   */
  getBedTemp(): number {
    return this.bed_temp;
  }

  /**
   * Get bed target temperature
   */
  getBedTargetTemp(): number {
    return this.bed_temp_target;
  }

  /**
   * Get chamber temperature
   */
  getChamberTemp(): number {
    return this.chamber_temp;
  }

  /**
   * Get chamber target temperature
   */
  getChamberTargetTemp(): number {
    return this.chamber_temp_target;
  }

  /**
   * Get frame temperature
   */
  getFrameTemp(): number {
    return this.frame_temp;
  }

  /**
   * Get nozzle temperature
   */
  getNozzleTemp(): number {
    return this.nozzle;
  }

  /**
   * Get nozzle target temperature
   */
  getNozzleTargetTemp(): number {
    return this.nozzle_target_temper || 0;
  }

  // ===== FAN METHODS =====

  /**
   * Get heatbreak fan speed
   */
  getHeatbreakFanSpeed(): number {
    return this.heatbreak_fan_speed;
  }

  /**
   * Get cooling fan speed
   */
  getCoolingFanSpeed(): number {
    return this.cooling_fan_speed;
  }

  /**
   * Get big fan 1 speed
   */
  getBigFan1Speed(): number {
    return this.big_fan1_speed;
  }

  /**
   * Get big fan 2 speed
   */
  getBigFan2Speed(): number {
    return this.big_fan2_speed;
  }

  /**
   * Get fan gear
   */
  getFanGear(): number {
    return this.fan_gear;
  }

  // ===== AMS ENHANCED METHODS =====

  /**
   * Check if AMS is unloading filament
   */
  isAmsUnload(): boolean {
    return this.m_tray_tar === "255";
  }

  /**
   * Check if AMS is on setting up
   */
  isAmsOnSettingup(): boolean {
    return this.ams_status_main === AmsStatusMain.AMS_STATUS_MAIN_ASSIST;
  }

  /**
   * Check if filament is moving
   */
  isFilamentMove(): boolean {
    return this.m_tray_now !== "255";
  }

  /**
   * Check if can unload filament
   */
  canUnloadFilament(): boolean {
    return this.hasAms() && this.isFilamentAtExtruder();
  }

  /**
   * Check if supports AMS/extruder mix mapping
   */
  isSupportAmxExtMixMapping(): boolean {
    return this.is_enable_np && this.is_enable_ams_np;
  }

  /**
   * Check if only external spool exists
   */
  onlyExistExtSpool(): boolean {
    return this.amsList.length === 0;
  }

  /**
   * Check if in extrusion calibration
   */
  isInExtrusionCali(): boolean {
    return this.ams_cali_stat > 0;
  }

  /**
   * Check if extrusion calibration is finished
   */
  isExtrusionCaliFinished(): boolean {
    return this.ams_cali_stat === 0;
  }

  // ===== MULTI-EXTRUDER METHODS =====

  /**
   * Check if printer has multiple extruders
   */
  isMultiExtruders(): boolean {
    return this.m_extder_data.total_extder_count > 1;
  }

  /**
   * Check if main extruder is on left
   */
  isMainExtruderOnLeft(): boolean {
    return this.m_extder_data.current_extder_id === 1;
  }

  /**
   * Get current extruder
   */
  getCurrentExtruder(): Extder | undefined {
    return this.m_extder_data.extders.find(ext => ext.id === this.m_extder_data.current_extder_id);
  }

  /**
   * Get extruder by ID
   */
  getExtruderById(id: number): Extder | undefined {
    return this.m_extder_data.extders.find(ext => ext.id === id);
  }

  /**
   * Check if nozzle flow type is supported
   */
  isNozzleFlowTypeSupported(): boolean {
    return this.is_enable_np;
  }

  /**
   * Get nozzle flow type for extruder
   */
  getNozzleFlowType(extruderId: number): NozzleFlowType {
    const extruder = this.getExtruderById(extruderId);
    return extruder?.current_nozzle_flow_type || NozzleFlowType.NONE_FLOWTYPE;
  }

  // ===== HARDWARE SUPPORT METHODS =====

  /**
   * Check if printer needs SD card
   */
  needSDCard(): boolean {
    return this.sdcard_state !== SdcardState.NO_SDCARD;
  }

  /**
   * Get SD card state
   */
  getSdcardState(): SdcardState {
    return this.sdcard_state;
  }

  /**
   * Check if printer supports chamber editing
   */
  isSupportChamberEdit(): boolean {
    return this.is_support_chamber_edit;
  }

  /**
   * Check if printer supports extrusion calibration
   */
  isSupportExtrusionCali(): boolean {
    return this.is_support_extrusion_cali;
  }

  /**
   * Check if printer supports first layer inspection
   */
  isSupportFirstLayerInspect(): boolean {
    return this.is_support_first_layer_inspect;
  }

  /**
   * Check if printer supports AI monitoring
   */
  isSupportAiMonitoring(): boolean {
    return this.is_support_ai_monitoring;
  }

  /**
   * Check if printer supports bed leveling
   */
  isSupportBedLeveling(): boolean {
    return this.is_support_bed_leveling > 0;
  }

  /**
   * Check if printer supports timelapse
   */
  isSupportTimelapse(): boolean {
    return this.is_support_timelapse;
  }

  /**
   * Check if printer supports auxiliary fan
   */
  isSupportAuxFan(): boolean {
    return this.is_support_aux_fan;
  }

  /**
   * Check if printer supports chamber fan
   */
  isSupportChamberFan(): boolean {
    return this.is_support_chamber_fan;
  }

  // ===== CAMERA & AI METHODS =====

  /**
   * Check if printer has IP camera
   */
  hasIpcam(): boolean {
    return this.has_ipcam;
  }

  /**
   * Check if camera is recording
   */
  isRecording(): boolean {
    return this.camera_recording;
  }

  /**
   * Check if camera recording is enabled
   */
  isRecordingEnable(): boolean {
    return this.camera_recording_when_printing;
  }

  /**
   * Check if timelapse is enabled
   */
  isTimelapse(): boolean {
    return this.camera_timelapse;
  }

  /**
   * Get camera resolution
   */
  getCameraResolution(): string {
    return this.camera_resolution;
  }

  /**
   * Get supported camera resolutions
   */
  getResolutionSupported(): string[] {
    return this.camera_resolution_supported;
  }

  /**
   * Check if first layer inspector is enabled
   */
  isFirstLayerInspector(): boolean {
    return this.xcam_first_layer_inspector;
  }

  /**
   * Check if AI monitoring is enabled
   */
  isAiMonitoring(): boolean {
    return this.xcam_ai_monitoring;
  }

  /**
   * Check if buildplate marker detector is enabled
   */
  isBuildplateMarkerDetector(): boolean {
    return this.xcam_buildplate_marker_detector;
  }

  // ===== UPGRADE & FIRMWARE METHODS =====

  /**
   * Check if printer is upgrading
   */
  isInUpgrading(): boolean {
    return this.upgrade_display_state === 2; // UPGRADING
  }

  /**
   * Check if upgrade is available
   */
  isUpgradingAvailable(): boolean {
    return this.upgrade_display_state === 1; // IDLE
  }

  /**
   * Get upgrade progress percentage
   */
  getUpgradePercent(): number {
    return parseInt(this.upgrade_progress) || 0;
  }

  /**
   * Get OTA version
   */
  getOtaVersion(): string {
    return this.ota_new_version_number;
  }

  /**
   * Check if version is valid
   */
  checkVersionValid(): boolean {
    return this.m_firmware_valid;
  }

  /**
   * Get firmware type string
   */
  getFirmwareTypeStr(): string {
    return this.firmware_type === 0 ? "engineer" : "production";
  }

  /**
   * Get lifecycle type string
   */
  getLifecycleTypeStr(): string {
    return this.lifecycle === 0 ? "engineer" : "production";
  }

  // ===== LIGHT METHODS =====

  /**
   * Get light effect string
   */
  getLightEffectStr(effect: LightEffect): string {
    switch (effect) {
      case LightEffect.LIGHT_EFFECT_ON:
        return "on";
      case LightEffect.LIGHT_EFFECT_OFF:
        return "off";
      case LightEffect.LIGHT_EFFECT_FLASHING:
        return "flashing";
      default:
        return "unknown";
    }
  }

  /**
   * Parse light effect from string
   */
  parseLightEffect(effectStr: string): LightEffect {
    switch (effectStr) {
      case "on":
        return LightEffect.LIGHT_EFFECT_ON;
      case "off":
        return LightEffect.LIGHT_EFFECT_OFF;
      case "flashing":
        return LightEffect.LIGHT_EFFECT_FLASHING;
      default:
        return LightEffect.LIGHT_EFFECT_UNKNOWN;
    }
  }

  // ===== DOOR OPEN CHECK METHODS =====

  /**
   * Check if door open check is supported
   */
  supportDoorOpenCheck(): boolean {
    return this.is_support_door_open_check;
  }

  /**
   * Get door open check state
   */
  getDoorOpenCheckState(): DoorOpenCheckState {
    return this.xcam_door_open_check;
  }

  // ===== UTILITY METHODS =====

  /**
   * Reset update time
   */
  resetUpdateTime(): void {
    this.last_update_time = Date.now();
  }

  /**
   * Reset all data
   */
  reset(): void {
    this.print_status = "";
    this.bed_temp = 0;
    this.bed_temp_target = 0;
    this.chamber_temp = 0;
    this.chamber_temp_target = 0;
    this.frame_temp = 0;
    this.nozzle = 0;
    this.mc_print_percent = 0;
    this.mc_left_time = 0;
    this.curr_layer = 0;
    this.total_layers = 0;
    this.resetUpdateTime();
  }

  /**
   * Check if printer is at heating mode
   */
  isAtHeatingMode(): boolean {
    return this.m_air_duct_data.curren_mode === 1; // AIR_DUCT_HEATING_INTERNAL_FILT
  }

  /**
   * Check if printer is using 220V
   */
  is220V(): boolean {
    return this.is_220V_voltage;
  }

  /**
   * Get bed temperature limit
   */
  getBedTemperatureLimit(): number {
    return this.bed_temperature_limit;
  }

  /**
   * Check if filament is installed
   */
  isFilamentInstalled(): boolean {
    return this.isFilamentAtExtruder();
  }

  /**
   * Check if axis is at home
   */
  isAxisAtHome(axis: string): boolean {
    // TODO: Implement axis home checking logic
    return true;
  }

  /**
   * Get current stage
   */
  getCurrStage(): string {
    // TODO: Implement stage string generation
    return "Unknown";
  }

  /**
   * Get current stage index
   */
  getCurrStageIdx(): number {
    return this.stage_curr;
  }

  /**
   * Check if in calibration
   */
  isInCalibration(): boolean {
    return this.calibration_done === false;
  }

  /**
   * Check if calibration is running
   */
  isCalibrationRunning(): boolean {
    return this.calibration_done === false && this.stage_curr > 0;
  }

  /**
   * Check if calibration is done
   */
  isCalibrationDone(): boolean {
    return this.calibration_done;
  }

  /**
   * Check if SD card printing
   */
  isSdcardPrinting(): boolean {
    return this.print_type === "system";
  }

  /**
   * Get liveview remote
   */
  getLiveviewRemote(): number {
    return this.liveview_remote;
  }

  /**
   * Get file remote
   */
  getFileRemote(): number {
    return this.file_remote;
  }

  /**
   * Check if can enable timelapse
   */
  canEnableTimelapse(): boolean {
    return this.is_support_timelapse && this.has_ipcam;
  }

  /**
   * Get compatible machines
   */
  getCompatibleMachine(): string[] {
    // TODO: Implement compatible machine detection
    return [];
  }

  /**
   * Get string from fan type
   */
  getStringFromFantype(type: FanType): string {
    switch (type) {
      case FanType.COOLING_FAN:
        return "cooling_fan";
      case FanType.BIG_COOLING_FAN:
        return "big_cooling_fan";
      case FanType.CHAMBER_FAN:
        return "chamber_fan";
      case FanType.EXHAUST_FAN:
        return "exhaust_fan";
      case FanType.FILTER_FAN:
        return "filter_fan";
      default:
        return "unknown";
    }
  }
}

/**
 * Check if AMS is waiting for filament load confirmation
 * @param ams_status The raw ams_status value from printer state
 * @returns true if waiting for confirmation, false otherwise
 * 
 * Status format:
 * - High byte (main status): 0x01 = AMS_STATUS_MAIN_FILAMENT_CHANGE
 * - Low byte (sub status): 0x06 = Waiting for confirmation
 * Example: status 260 (0x104) = Waiting for confirmation
 */
export function isWaitingFilamentConfirmation(ams_status: number): boolean {
    const ams_status_main = (ams_status & 0xFF00) >> 8;
    const ams_status_sub = ams_status & 0xFF;
    
    return (ams_status_main === AmsStatusMain.AMS_STATUS_MAIN_FILAMENT_CHANGE) && 
           (ams_status_sub === 0x06);
}

/**
 * Calculates filament backup groups based on compatible filaments in AMS slots.
 * Compatible filaments must have the same type and color.
 * 
 * @param printerData - The printer data containing AMS information
 * @returns Array of bitmap values where each bit represents an AMS slot in that backup group
 */
interface FilamentBackupGroup {
    slots: number[];  // Array of slot indices that are compatible
}

/**
 * Parses filam_bak bitmaps to determine which filament slots are compatible backups
 * @param filam_bak Array of bitmaps where each bit represents an AMS slot
 * @returns Array of FilamentBackupGroup objects, each containing compatible slot indices
 * 
 * @example
 * // Input: [3, 12]  (binary: 0011 and 1100)
 * // Returns: [
 * //   { slots: [0, 1] },   // Slots 0 and 1 are compatible
 * //   { slots: [2, 3] }    // Slots 2 and 3 are compatible
 * // ]
 */
export function parseFilamBak(filam_bak: number[]): FilamentBackupGroup[] {
    if (!Array.isArray(filam_bak)) {
        return [];
    }
    const groups: FilamentBackupGroup[] = [];
    
    for (const bitmap of filam_bak) {
        const group: FilamentBackupGroup = {
            slots: []
        };
        
        // Check each bit position (0-15 for up to 16 slots)
        for (let i = 0; i < 16; i++) {
            // If bit is set (1), this slot is part of the group
            if ((bitmap & (1 << i)) !== 0) {
                group.slots.push(i);
            }
        }
        
        // Only add groups with 2 or more compatible slots
        if (group.slots.length >= 2) {
            groups.push(group);
        }
    }
    
    return groups;
}

export interface BackupSlot {
    ams_id: number;
    tray_id: number;
}

export interface EnrichedTray {
    ams_id: number;
    tray_id: number;
    type: string;
    color: string;
    backup_slots?: BackupSlot[];  // Other slots in the same backup group
}

export interface EnrichedAms {
    id: number;
    trays: EnrichedTray[];
}

export function getEnrichedAmsData(printerData: PushAllResponse): EnrichedAms[] {
    const enrichedAms: EnrichedAms[] = [];
    
    if (!printerData.ams?.ams) return enrichedAms;
    
    // Parse filament backup groups
    const backupGroups = parseFilamBak(printerData.filam_bak);
    
    printerData.ams.ams.forEach((ams, amsIndex) => {
        if (!ams?.tray) return;
        
        const enrichedUnit: EnrichedAms = {
            id: amsIndex,
            trays: []
        };
        
        ams.tray.forEach((tray, trayIndex) => {
            if (!tray) return;
            
            const globalSlotIndex = (amsIndex * 4) + trayIndex;
            
            // Find backup slots for this tray
            const backupSlots: BackupSlot[] = [];
            
            backupGroups.forEach(group => {
                if (group.slots.includes(globalSlotIndex)) {
                    group.slots.forEach(slotIndex => {
                        if (slotIndex !== globalSlotIndex) {
                            const backupAmsId = Math.floor(slotIndex / 4);
                            const backupTrayId = slotIndex % 4;
                            backupSlots.push({
                                ams_id: backupAmsId,
                                tray_id: backupTrayId
                            });
                        }
                    });
                }
            });
            
            enrichedUnit.trays.push({
                ams_id: amsIndex,
                tray_id: trayIndex,
                type: tray.tray_type || '',
                color: tray.tray_color || '',
                backup_slots: backupSlots.length > 0 ? backupSlots : undefined
            });
        });
        
        enrichedAms.push(enrichedUnit);
    });
    
    return enrichedAms;
}

export function calculateFilamBak(printerData: PushAllResponse): number[] {
    const filamBak: number[] = [];
    
    if (!printerData.ams?.ams) return filamBak;
    
    // Build a map of filament groups by type and color
    const filamentGroups = new Map<string, number[]>();
    
    printerData.ams.ams.forEach((ams, amsIndex) => {
        if (!ams?.tray) return;
        
        ams.tray.forEach((tray, trayIndex) => {
            if (!tray) return;
            
            const globalSlotIndex = (amsIndex * 4) + trayIndex;
            const key = `${tray.tray_type}-${tray.tray_color}`;
            
            if (!filamentGroups.has(key)) {
                filamentGroups.set(key, []);
            }
            filamentGroups.get(key)!.push(globalSlotIndex);
        });
    });
    
    // Convert groups to bitmaps
    filamentGroups.forEach((slots) => {
        if (slots.length > 1) {
            let bitmap = 0;
            slots.forEach(slotIndex => {
                bitmap |= (1 << slotIndex);
            });
            filamBak.push(bitmap);
        }
    });
    
    return filamBak;
}

// ===== DUAL NOZZLE AMS MAPPING UTILITIES =====

/**
 * Project filament information including nozzle assignment
 */
export interface ProjectFilament {
  /** Filament index (0-based) in the project */
  id: number;
  /** Material type (e.g., "PLA", "PETG") */
  type: string;
  /** Color in hex format (#RRGGBB) */
  color: string;
  /** Length of filament used in meters */
  used_m?: number;
  /** Weight of filament used in grams */
  used_g?: number;
  /** Tray info index from the 3MF file */
  tray_info_idx: string;
  /** Nozzle assignment (1 = nozzle 1, 2 = nozzle 2) */
  nozzle: number;
}

/**
 * AMS slot information for mapping
 */
export interface AmsSlotInfo {
  /** Slot index (0-15 for up to 4 AMS units) */
  slot: number;
  /** Material type */
  type: string;
  /** Color in hex format */
  color: string;
  /** Slot ID/identifier */
  id: string;
  /** Filament ID */
  filamentId: string;
  /** Optional vendor information */
  vendor?: string;
  /** Optional remaining filament in meters */
  remaining_m?: number;
}

/**
 * AMS setup containing all available slots
 */
export interface AmsConfiguration {
  /** Map of slot index to slot information */
  slots: Record<number, AmsSlotInfo>;
}

/**
 * Color matching details for UI display
 */
export interface ColorMatchDetails {
  hueDiff: number;
  satDiff: number;
  lightDiff: number;
  totalDistance: number;
  saturationPenalty: number;
}

/**
 * Mapping information for a single filament
 */
export interface FilamentMappingInfo {
  /** AMS slot index */
  ams: number;
  /** Target color in the AMS slot */
  targetColor: string;
  /** Filament type in the AMS slot */
  filamentType: string;
  /** Filament ID setting */
  filamentId: string;
  /** Source color from the project */
  sourceColor: string;
  /** Match quality indicator */
  matchQuality: 'exact' | 'color' | 'type' | 'none';
  /** Optional detailed color matching information */
  details?: ColorMatchDetails;
  /** Optional reason for the match */
  reason?: string;
}

/**
 * Complete AMS mapping result
 */
export interface AmsMappingResult {
  /** Array where index is filament ID and value is AMS slot (-1 if unmapped) */
  ams_mapping: number[];
  /** Detailed mapping information for each filament */
  ams_mapping_info: FilamentMappingInfo[];
  /** Whether to use AMS for this print */
  task_use_ams: boolean;
}

/**
 * Filaments grouped by nozzle
 */
export interface FilamentsByNozzle {
  /** Map of nozzle number to array of filaments */
  [nozzle: number]: ProjectFilament[];
}

/**
 * AMS mapping2 entry - separates AMS unit from slot
 */
export interface AmsMapping2Entry {
  /** AMS unit ID (0-3) */
  ams_id: number;
  /** Slot ID within the AMS (0-3) */
  slot_id: number;
}

/**
 * Dual nozzle AMS mapping result
 */
export interface DualNozzleAmsMappingResult {
  /** Complete AMS mapping for all filaments */
  ams_mapping: number[];
  /** AMS mapping2 format - array of {ams_id, slot_id} objects */
  ams_mapping2: AmsMapping2Entry[];
  /** Mapping information for each filament */
  ams_mapping_info: FilamentMappingInfo[];
  /** Whether to use AMS */
  task_use_ams: boolean;
  /** Filaments grouped by nozzle */
  filamentsByNozzle: FilamentsByNozzle;
  /** Number of nozzles used (1 or 2) */
  nozzleCount: number;
  /** Whether this is a dual nozzle print */
  isDualNozzle: boolean;
}

/**
 * Slice info plate data containing filament maps
 */
export interface SliceInfoPlate {
  index?: number;
  nozzle_diameters?: number | number[];
  filament_maps?: number | number[];
  extruder_type?: string | string[];
  filaments?: Array<{
    id: number;
    type: string;
    color: string;
    tray_info_idx: string;
    used_m?: number;
    used_g?: number;
  }>;
}

/**
 * Parse filament_maps from slice info to determine nozzle assignments
 *
 * @param filament_maps - Can be a number, array of numbers, or space-separated string
 * @returns Array of nozzle assignments where index is filament ID and value is nozzle number
 *
 * @example
 * // Single nozzle (all filaments use nozzle 1)
 * parseFilamentMaps("1 1 1 1") // [1, 1, 1, 1]
 * parseFilamentMaps([1, 1, 1, 1]) // [1, 1, 1, 1]
 *
 * // Dual nozzle (alternating nozzles)
 * parseFilamentMaps("1 2 1 2") // [1, 2, 1, 2]
 * parseFilamentMaps([1, 2, 1, 2]) // [1, 2, 1, 2]
 */
export function parseFilamentMaps(filament_maps: number | number[] | string | undefined): number[] {
  if (!filament_maps) {
    return [];
  }

  // Already an array
  if (Array.isArray(filament_maps)) {
    return filament_maps;
  }

  // Single number - treat as one filament
  if (typeof filament_maps === 'number') {
    return [filament_maps];
  }

  // String - split by space and convert to numbers
  if (typeof filament_maps === 'string') {
    return filament_maps
      .trim()
      .split(/\s+/)
      .map(num => parseInt(num, 10))
      .filter(num => !isNaN(num));
  }

  return [];
}

/**
 * Group project filaments by nozzle assignment
 *
 * @param filaments - Array of project filaments
 * @param filament_maps - Nozzle assignments for each filament
 * @returns Object with nozzle numbers as keys and arrays of filaments as values
 *
 * @example
 * const filaments = [
 *   { id: 0, type: "PLA", color: "#FF0000", ...otherProps },
 *   { id: 1, type: "PETG", color: "#00FF00", ...otherProps },
 * ];
 * const filament_maps = [1, 2]; // Filament 0 uses nozzle 1, filament 1 uses nozzle 2
 *
 * groupFilamentsByNozzle(filaments, filament_maps);
 * // Returns: {
 * //   1: [{ id: 0, type: "PLA", color: "#FF0000", nozzle: 1, ... }],
 * //   2: [{ id: 1, type: "PETG", color: "#00FF00", nozzle: 2, ... }]
 * // }
 */
export function groupFilamentsByNozzle(
  filaments: Array<Omit<ProjectFilament, 'nozzle'>>,
  filament_maps: number[]
): FilamentsByNozzle {
  const grouped: FilamentsByNozzle = {};

  filaments.forEach((filament, index) => {
    const nozzle = filament_maps[index] || 1; // Default to nozzle 1 if not specified

    if (!grouped[nozzle]) {
      grouped[nozzle] = [];
    }

    grouped[nozzle].push({
      ...filament,
      nozzle,
    });
  });

  return grouped;
}

// Color utilities (hexToRgb, getColorDistance, etc.) are imported from ./colorUtils

/**
 * Convert global AMS slot index to AMS unit and slot IDs
 *
 * @param globalSlot - Global slot index (0-15 for up to 4 AMS units, 254-255 for virtual trays)
 * @returns Object with ams_id and slot_id
 *
 * @example
 * globalSlotToAmsMapping2Entry(0)   // { ams_id: 0, slot_id: 0 }
 * globalSlotToAmsMapping2Entry(5)   // { ams_id: 1, slot_id: 1 }
 * globalSlotToAmsMapping2Entry(15)  // { ams_id: 3, slot_id: 3 }
 * globalSlotToAmsMapping2Entry(254) // { ams_id: 254, slot_id: 0 } - Virtual tray (external spool)
 * globalSlotToAmsMapping2Entry(255) // { ams_id: 255, slot_id: 0 } - Virtual tray (external spool)
 */
export function globalSlotToAmsMapping2Entry(globalSlot: number): AmsMapping2Entry {
  // Handle virtual slots (254, 255) for external spools on dual-nozzle printers
  // Virtual slots use the global slot value as ams_id and slot_id = 0
  // See: AMSFilamentSettingCommand in @hiv3d/bambu-node
  if (globalSlot >= 254) {
    return {
      ams_id: globalSlot,  // 254 or 255
      slot_id: 0,
    };
  }

  // Regular AMS slots (0-15): standard conversion
  return {
    ams_id: Math.floor(globalSlot / 4),
    slot_id: globalSlot % 4,
  };
}

/**
 * Convert AMS unit and slot IDs to global slot index
 *
 * @param ams_id - AMS unit ID (0-3 for regular AMS, 254-255 for virtual trays)
 * @param slot_id - Slot ID within AMS (0-3)
 * @returns Global slot index (0-15 for regular AMS, 254-255 for virtual trays)
 *
 * @example
 * amsMapping2EntryToGlobalSlot(0, 0)    // 0
 * amsMapping2EntryToGlobalSlot(1, 1)    // 5
 * amsMapping2EntryToGlobalSlot(3, 3)    // 15
 * amsMapping2EntryToGlobalSlot(254, 0)  // 254 - Virtual tray (external spool)
 * amsMapping2EntryToGlobalSlot(255, 0)  // 255 - Virtual tray (external spool)
 */
export function amsMapping2EntryToGlobalSlot(ams_id: number, slot_id: number): number {
  // Handle virtual slots (254, 255) - return ams_id directly
  if (ams_id >= 254) {
    return ams_id;
  }

  // Regular AMS slots: standard conversion
  return ams_id * 4 + slot_id;
}

export const globalSlotFromAms = amsMapping2EntryToGlobalSlot;

/**
 * Convert ams_mapping array to ams_mapping2 format
 *
 * Both arrays must have the same length to match BambuStudio behavior.
 * Unmapped filaments (-1) are converted to {ams_id: 255, slot_id: 255} (VIRTUAL_TRAY_MAIN_ID).
 *
 * @param ams_mapping - Array where index is filament ID and value is global slot
 * @returns Array of AmsMapping2Entry objects with same length as input
 *
 * @example
 * convertToAmsMapping2([0, 5, -1, 15])
 * // [
 * //   { ams_id: 0, slot_id: 0 },
 * //   { ams_id: 1, slot_id: 1 },
 * //   { ams_id: 255, slot_id: 255 },  // unmapped
 * //   { ams_id: 3, slot_id: 3 }
 * // ]
 */
export function convertToAmsMapping2(ams_mapping: (number | null | undefined)[]): AmsMapping2Entry[] {
  // Use Array.from to handle sparse arrays (holes become undefined, not skipped)
  return Array.from({ length: ams_mapping.length }, (_, i) => {
    const globalSlot = ams_mapping[i];
    // Treat null, undefined, and -1 as unmapped
    if (globalSlot == null || globalSlot === -1) {
      return { ams_id: VIRTUAL_TRAY_MAIN_ID, slot_id: VIRTUAL_TRAY_MAIN_ID };
    }
    return globalSlotToAmsMapping2Entry(globalSlot);
  });
}

/**
 * Convert ams_mapping2 format to ams_mapping array
 *
 * Both arrays must have the same length to match BambuStudio behavior.
 * {ams_id: 255, slot_id: 255} represents an unmapped filament and is converted to -1.
 *
 * @param ams_mapping2 - Array of AmsMapping2Entry objects
 * @param totalFilaments - Total number of filaments in the project (deprecated, uses array length)
 * @returns Array where index is filament ID and value is global slot
 *
 * @example
 * convertFromAmsMapping2(
 *   [{ ams_id: 0, slot_id: 0 }, { ams_id: 255, slot_id: 255 }, { ams_id: 1, slot_id: 1 }],
 *   3
 * ) // [0, -1, 5]
 */
export function convertFromAmsMapping2(
  ams_mapping2: AmsMapping2Entry[],
  totalFilaments: number
): number[] {
  // Use ams_mapping2 length to ensure arrays are same size
  const mapping = new Array(ams_mapping2.length).fill(-1);
  ams_mapping2.forEach((entry, index) => {
    // Unmapped filaments: {ams_id: 255, slot_id: 255} -> -1
    if (entry.ams_id === VIRTUAL_TRAY_MAIN_ID && entry.slot_id === VIRTUAL_TRAY_MAIN_ID) {
      mapping[index] = -1;
    } else {
      mapping[index] = amsMapping2EntryToGlobalSlot(entry.ams_id, entry.slot_id);
    }
  });
  return mapping;
}

/**
 * Find the best AMS slot for a given filament
 *
 * @param filament - Filament to match
 * @param amsSetup - Available AMS slots
 * @param usedSlots - Set of already used slot indices
 * @param colorThreshold - Maximum color distance for a match (default: 15)
 * @returns Object containing slot index and match quality
 */
export function findBestAmsSlotForFilament(
  filament: Pick<ProjectFilament, 'type' | 'color'>,
  amsSetup: AmsConfiguration,
  usedSlots: Set<number> = new Set(),
  colorThreshold: number = 15
): { slot: number; matchQuality: FilamentMappingInfo['matchQuality']; reason?: string } {
  const availableSlots = Object.entries(amsSetup.slots)
    .filter(([slot]) => !usedSlots.has(parseInt(slot)))
    .map(([_slot, info]) => ({
      ...info,
    }));

  // Try exact match first
  const exactMatch = availableSlots.find(
    slot =>
      slot.type.toLowerCase() === filament.type.toLowerCase() &&
      slot.color.toLowerCase() === filament.color.toLowerCase()
  );
  if (exactMatch) {
    return {
      slot: exactMatch.slot,
      matchQuality: 'exact',
      reason: 'Exact match found for both type and color',
    };
  }

  // Try same type, closest color
  const sameTypeSlots = availableSlots.filter(
    slot => slot.type.toLowerCase() === filament.type.toLowerCase()
  );

  if (sameTypeSlots.length > 0) {
    const withDistance = sameTypeSlots.map(slot => ({
      ...slot,
      distance: getColorDistance(filament.color, slot.color),
    }));

    withDistance.sort((a, b) => a.distance - b.distance);

    if (withDistance[0].distance <= colorThreshold) {
      return {
        slot: withDistance[0].slot,
        matchQuality: 'color',
        reason: `Color match (ΔE76=${withDistance[0].distance.toFixed(1)})`,
      };
    }
  }

  // Try just matching type
  const typeMatch = availableSlots.find(
    slot => slot.type.toLowerCase() === filament.type.toLowerCase()
  );
  if (typeMatch) {
    return {
      slot: typeMatch.slot,
      matchQuality: 'type',
      reason: 'Only material type matches, colors are significantly different',
    };
  }

  return {
    slot: -1,
    matchQuality: 'none',
    reason: 'No matching material type found in available slots',
  };
}

/**
 * Create AMS mapping for dual nozzle printer
 *
 * This function creates a complete AMS mapping for prints that may use one or two nozzles.
 * It automatically detects whether the print is single or dual nozzle based on filament_maps,
 * groups filaments by nozzle, and creates optimal mappings for each nozzle.
 *
 * @param filaments - Array of filaments from the project (without nozzle assignment)
 * @param filament_maps - Nozzle assignments from slice_info
 * @param amsSetup - Available AMS configuration
 * @param colorThreshold - Maximum color distance for matching (default: 15)
 * @returns Complete dual nozzle AMS mapping result
 *
 * @example
 * // Single nozzle print
 * const singleNozzleResult = createDualNozzleAmsMapping(
 *   [
 *     { id: 0, type: "PLA", color: "#FF0000", tray_info_idx: "0", used_g: 10 },
 *     { id: 1, type: "PLA", color: "#00FF00", tray_info_idx: "1", used_g: 15 },
 *   ],
 *   [1, 1], // Both filaments use nozzle 1
 *   amsSetup
 * );
 *
 * // Dual nozzle print
 * const dualNozzleResult = createDualNozzleAmsMapping(
 *   [
 *     { id: 0, type: "PLA", color: "#FF0000", tray_info_idx: "0", used_g: 10 },
 *     { id: 1, type: "PETG", color: "#00FF00", tray_info_idx: "1", used_g: 15 },
 *     { id: 2, type: "PLA", color: "#0000FF", tray_info_idx: "2", used_g: 8 },
 *   ],
 *   [1, 2, 1], // Filaments 0 and 2 use nozzle 1, filament 1 uses nozzle 2
 *   amsSetup
 * );
 */
export function createDualNozzleAmsMapping(
  filaments: Array<Omit<ProjectFilament, 'nozzle'>>,
  filament_maps: number | number[] | string,
  amsSetup: AmsConfiguration,
  colorThreshold: number = 15
): DualNozzleAmsMappingResult {
  // Parse filament maps
  const parsedFilamentMaps = parseFilamentMaps(filament_maps);

  // Group filaments by nozzle
  const filamentsByNozzle = groupFilamentsByNozzle(filaments, parsedFilamentMaps);

  // Determine if dual nozzle
  const nozzleNumbers = Object.keys(filamentsByNozzle).map(Number);
  const isDualNozzle = nozzleNumbers.length > 1;
  const nozzleCount = nozzleNumbers.length;

  // Initialize mapping arrays
  // Size to max filament ID to avoid sparse holes (null in JSON) when filament.id > filaments.length
  const maxFilamentId = filaments.reduce((max, f) => Math.max(max, f.id), -1);
  const ams_mapping: number[] = new Array(Math.max(filaments.length, maxFilamentId + 1)).fill(-1);
  const ams_mapping_info: FilamentMappingInfo[] = [];
  const usedSlots = new Set<number>();

  // Sort nozzles to process nozzle 1 first, then nozzle 2
  nozzleNumbers.sort();

  // Process each nozzle's filaments
  nozzleNumbers.forEach(nozzleNum => {
    const nozzleFilaments = filamentsByNozzle[nozzleNum];

    // Sort by usage if available (prioritize heavily used filaments)
    const sortedFilaments = [...nozzleFilaments].sort((a, b) => {
      const usageA = a.used_g || a.used_m || 0;
      const usageB = b.used_g || b.used_m || 0;
      return usageB - usageA;
    });

    sortedFilaments.forEach(filament => {
      const matchResult = findBestAmsSlotForFilament(
        filament,
        amsSetup,
        usedSlots,
        colorThreshold
      );

      if (matchResult.slot >= 0) {
        ams_mapping[filament.id] = matchResult.slot;
        usedSlots.add(matchResult.slot);

        ams_mapping_info.push({
          ams: matchResult.slot,
          targetColor: amsSetup.slots[matchResult.slot].color,
          filamentType: amsSetup.slots[matchResult.slot].type,
          filamentId: amsSetup.slots[matchResult.slot].filamentId || getDefaultFilamentIdForMaterialType(filament.type),
          sourceColor: filament.color,
          matchQuality: matchResult.matchQuality,
          reason: matchResult.reason,
        });
      }
    });
  });

  // Convert to ams_mapping2 format
  const ams_mapping2 = convertToAmsMapping2(ams_mapping);

  return {
    ams_mapping,
    ams_mapping2,
    ams_mapping_info,
    task_use_ams: true,
    filamentsByNozzle,
    nozzleCount,
    isDualNozzle,
  };
}

/**
 * Helper function to extract filament data from slice info and create mappings
 *
 * This is a convenience function that combines parsing slice info with creating AMS mappings.
 *
 * @param sliceInfoPlate - Plate data from slice_info.config
 * @param amsSetup - Available AMS configuration
 * @param colorThreshold - Maximum color distance for matching (default: 15)
 * @returns Complete dual nozzle AMS mapping result
 *
 * @example
 * const sliceInfoPlate = {
 *   filament_maps: "1 2 1",
 *   filaments: [
 *     { id: 0, type: "PLA", color: "#FF0000", tray_info_idx: "0", used_g: 10 },
 *     { id: 1, type: "PETG", color: "#00FF00", tray_info_idx: "1", used_g: 15 },
 *     { id: 2, type: "PLA", color: "#0000FF", tray_info_idx: "2", used_g: 8 },
 *   ]
 * };
 *
 * const result = createAmsMappingFromSliceInfo(sliceInfoPlate, amsSetup);
 */
export function createAmsMappingFromSliceInfo(
  sliceInfoPlate: SliceInfoPlate,
  amsSetup: AmsConfiguration,
  colorThreshold: number = 15
): DualNozzleAmsMappingResult | null {
  if (!sliceInfoPlate.filaments || !sliceInfoPlate.filament_maps) {
    return null;
  }

  const filaments = sliceInfoPlate.filaments.map(f => ({
    id: f.id,
    type: f.type,
    color: f.color,
    tray_info_idx: f.tray_info_idx,
    used_m: f.used_m,
    used_g: f.used_g,
  }));

  return createDualNozzleAmsMapping(
    filaments,
    sliceInfoPlate.filament_maps,
    amsSetup,
    colorThreshold
  );
}

/**
 * Get a human-readable summary of the mapping
 *
 * @param mapping - Dual nozzle AMS mapping result
 * @returns Object with summary information
 *
 * @example
 * const summary = getAmsMappingSummary(mappingResult);
 * console.log(summary);
 * // {
 * //   nozzleCount: 2,
 * //   isDualNozzle: true,
 * //   totalFilaments: 3,
 * //   mappedFilaments: 3,
 * //   unmappedFilaments: 0,
 * //   nozzle1Filaments: 2,
 * //   nozzle2Filaments: 1,
 * //   exactMatches: 2,
 * //   colorMatches: 1,
 * //   typeMatches: 0,
 * //   noMatches: 0
 * // }
 */
export function getAmsMappingSummary(mapping: DualNozzleAmsMappingResult) {
  const totalFilaments = mapping.ams_mapping.length;
  const mappedFilaments = mapping.ams_mapping.filter(slot => slot !== -1).length;
  const unmappedFilaments = totalFilaments - mappedFilaments;

  const nozzle1Filaments = mapping.filamentsByNozzle[1]?.length || 0;
  const nozzle2Filaments = mapping.filamentsByNozzle[2]?.length || 0;

  const matchQualityCounts = {
    exact: 0,
    color: 0,
    type: 0,
    none: 0,
  };

  mapping.ams_mapping_info.forEach(info => {
    matchQualityCounts[info.matchQuality]++;
  });

  return {
    nozzleCount: mapping.nozzleCount,
    isDualNozzle: mapping.isDualNozzle,
    totalFilaments,
    mappedFilaments,
    unmappedFilaments,
    nozzle1Filaments,
    nozzle2Filaments,
    exactMatches: matchQualityCounts.exact,
    colorMatches: matchQualityCounts.color,
    typeMatches: matchQualityCounts.type,
    noMatches: matchQualityCounts.none,
  };
}